import React, { useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  ListItemText,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import _ from "lodash";
import moment from "moment";

const PromptSelectDropdown = ({
  responsePrompts,
  promptId,
  promptName,
  setPromptId,
  setPromptType,
  setPromptName,
  chatData = false,
  isReviews = false,
  isSentiment = false,
  isPrompt = false,
  label = "Prompt Name",
  fullWidth = true,
  size = "small",
  sx = { marginTop: "-8px" },
  disabled = false,
}) => {

  // Create grouped and sorted prompts list
  const promptsSelectList = useMemo(() => {
    const data = _.map(
      _.filter(_.clone(responsePrompts), it => {

        if(it?.status ===3) return false;

        if(chatData) return it?.type === "Chat";
        
        return it?.type !== "Chat";
      }), 
      a => {
        let temp = _.clone(a);
        const t = a.type + (a.subType ? ` / ${a.subType}` : "");
        return { ...temp, subName: t };
      }
    );
    const groupedSortedPrompts = _.groupBy(data, 'subName');
    const objArr = _.map(_.keys(groupedSortedPrompts), it => ({ 
      name: it, 
      data: _.orderBy(_.filter(data, a => a.subName === it), 'name', 'asc') 
    }));
    return _.orderBy(objArr, ['subName'], ['asc']);
  }, [responsePrompts, chatData]);

  // Get display name for selected prompt
  const getPromptDisplayName = (id) => {
    let data = null;
    promptsSelectList?.forEach(group => {
      const found = group.data.find(prompt => prompt.promptId === id);
      if (found) {
        data = found;
      }
    });
    
    if (data?.promptId) {
      return `${data?.name} ${data?.version ? `(v${data?.version})` : ""}`;
    }
    return "";
  };

  // Handle prompt selection
  const handlePromptSelection = (prompt) => {
    setPromptId(prompt?.promptId);
    setPromptType(prompt?.type);
    setPromptName(prompt?.name);
    
    // Store selection in localStorage based on context
    const promptObj = { 
      name: prompt?.name, 
      promptId: prompt?.promptId, 
      type: prompt?.type 
    };
    
    const storageKey = isReviews 
      ? 'reviewPrompt' 
      : isSentiment 
        ? 'sentimentPrompt' 
        : isPrompt 
          ? 'promptPrompt' 
          : chatData 
            ? 'chatPrompt' 
            : '';
            
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(promptObj));
    }
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth sx={sx} disabled={disabled}>
      <InputLabel size="small">
        {label}
      </InputLabel>
      <Select
        size={"small"}
        label={label}
        value={promptId ?? ""}
        renderValue={(value) => getPromptDisplayName(value)}
        MenuProps={{ 
          PaperProps: { 
            sx: { maxHeight: '500px', overflowY: 'auto' } 
          } 
        }}
      >
        <MenuItem  value={null} onClick={() => handlePromptSelection(null)} sx={{minHeight:'33px !important'}}></MenuItem>
        {promptsSelectList?.reduce((acc, p) => {
          // Add subheader
          acc.push(
            <ListSubheader 
              key={`subheader-${p.name}`} 
              sx={{ fontSize: '18px', fontWeight: 600 }}
            >
              {p.name}
            </ListSubheader>
          );
          
          // Add menu items for this group
          acc.push(
            ...p.data.map(prompt => (
              <MenuItem
                sx={{ display: "flex", justifyContent: "space-between" }}
                key={prompt.name}
                value={prompt.promptId}
                onClick={() => handlePromptSelection(prompt)}
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      sx={{ width: "100%", mr: 3.5 }}
                      spacing={2}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Typography>
                          {prompt.name}
                        </Typography>
                        {prompt?.version && (
                          <Typography color="text.disabled">
                            {"(v" + prompt?.version + ")"}
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction={'row'} spacing={2} alignItems={'center'}>
                        <Typography color="text.disabled">
                          {prompt.llm}
                        </Typography>
                        <Tooltip 
                          disableInteractive 
                          arrow 
                          title={prompt?.createdOn && prompt?.modifiedOn ? (
                            <Stack spacing={.5}>
                              <div>Created by: {prompt?.createdBy}</div>
                              <div>Created on: {moment(prompt?.createdOn).format('MMMM Do YYYY, h:mm:ss a')}</div>
                              <div>Modified by: {prompt?.modifiedBy}</div>
                              <div>Modified on: {moment(prompt?.modifiedOn).format('MMMM Do YYYY, h:mm:ss a')}</div>
                            </Stack>
                          ) : ''}
                        >
                          <span>
                            <PersonOutlineOutlinedIcon 
                              fontSize="small" 
                              sx={{ color: theme => theme.palette.text.disabled }} 
                            />
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  }
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </MenuItem>
            ))
          );
          return acc;
        }, [])}
      </Select>
    </FormControl>
  );
};

export default PromptSelectDropdown;